import { useEffect, useState } from 'react'
import { RefreshCw, Users, Search, Medal } from 'lucide-react'
import { api } from '../lib/api'
import { useAuthStore } from '../store/authStore'
import clsx from 'clsx'

interface Member {
  id: string
  name: string
  email: string
  role: 'admin' | 'manager' | 'employee'
  preferred_channel: 'slack' | 'teams' | 'email'
  nudge_frequency: 'daily' | 'weekly' | 'off'
  nudge_track: string | null
  slack_user_id: string | null
  completed_this_month: number
}

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-[#EDE8F5] text-ink border-[#d4cce8]',
  manager: 'bg-[#EBF6FB] text-brand-teal border-[#a7d8e0]',
  employee: 'bg-warm-inset text-ink-muted border-warm-border',
}

const FREQ_COLORS: Record<string, string> = {
  daily: 'text-emerald-800',
  weekly: 'text-brand-teal',
  off: 'text-ink-soft',
}

const TRACK_OPTIONS = [
  'leadership',
  'inclusion',
  'burnout',
  'safety',
  'recognition',
  'goals',
] as const

function memberDisplayName(m: { name: string; email: string }): string {
  const n = m.name?.trim()
  if (n && n !== 'undefined' && n !== 'null') return n
  const local = m.email?.split('@')[0]?.trim()
  if (local && local !== 'undefined' && local !== 'null') return local
  return 'Member'
}

const TRACK_STYLES: Record<string, string> = {
  leadership:  'bg-[#EBF6FB] text-ink border-[#a7d8e0]',
  inclusion:   'bg-[#eef5ec] text-ink border-[#B8C7B1]',
  burnout:     'bg-[#fdf2f2] text-ink border-[#e8cfcf]',
  safety:      'bg-[#EDE8F5] text-ink border-[#d4cce8]',
  recognition: 'bg-[#fdf8f0] text-ink border-[#e8d4b8]',
  goals:       'bg-[#E8F0EE] text-ink border-[#B8C7B1]',
}

export function TeamPage() {
  const { user } = useAuthStore()
  const isManager = user?.role === 'manager' || user?.role === 'admin'

  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<string | null>(null)
  const [trackSaving, setTrackSaving] = useState<string | null>(null) // userId being saved
  const [trackError, setTrackError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  async function loadMembers(): Promise<Member[]> {
    setLoading(true)
    setLoadError(null)
    try {
      const r = await api.getMembers() as { members: Member[] }
      const list = Array.isArray(r.members) ? r.members : []
      setMembers(list)
      return list
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load team members'
      setLoadError(msg)
      setMembers([])
      return []
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadMembers()
  }, [])

  function formatSyncMessage(res: { newMembers: number; processed?: number; totalMembers: number }): string {
    const { newMembers, processed = 0, totalMembers } = res
    if (newMembers > 0) {
      return `Added ${newMembers} new member(s). Total: ${totalMembers}.`
    }
    if (processed > 0) {
      return `Refreshed ${processed} member(s) from Slack. Total: ${totalMembers}.`
    }
    return `Team is up to date. Total: ${totalMembers}.`
  }

  async function handleSync() {
    setSyncing(true)
    setSyncResult(null)
    try {
      const res = await api.syncMembers() as { newMembers: number; processed?: number; totalMembers: number }
      setSyncResult(formatSyncMessage(res))
      let list = await loadMembers()
      if (res.totalMembers > 0 && list.length === 0) {
        await new Promise(r => setTimeout(r, 400))
        list = await loadMembers()
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Unknown error'
      setSyncResult(msg === 'Unauthorized' ? 'Sync failed — sign in again.' : `Sync failed — ${msg}`)
    } finally {
      setSyncing(false)
    }
  }

  async function handleTrackChange(memberId: string, track: string | null) {
    // Optimistic update
    setMembers((prev: Member[]) => prev.map((m: Member) => m.id === memberId ? { ...m, nudge_track: track } : m))
    setTrackSaving(memberId)
    setTrackError(null)
    try {
      await api.updateMember(memberId, { nudgeTrack: track })
    } catch (e: unknown) {
      // Revert on failure
      void loadMembers()
      setTrackError(e instanceof Error ? e.message : 'Failed to update track')
    } finally {
      setTrackSaving(null)
    }
  }

  const filteredMembers = members.filter((m: Member) => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const columns = isManager
    ? ['Member', 'Role', 'Channel', 'Frequency', 'Nudge Track', 'Connected']
    : ['Member', 'Role', 'Channel', 'Frequency', 'Nudge Track', 'Connected']

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Team</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>{members.length} members receiving nudges</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" size={14} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 pl-9 pr-4 py-2 bg-warm-card border border-warm-border rounded-lg text-sm text-ink outline-none focus:border-brand-teal transition-colors shadow-card"
            />
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 bg-warm-card hover:bg-warm-inset border border-warm-border text-ink font-medium text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50 shadow-card whitespace-nowrap"
          >
            <RefreshCw size={14} className={clsx(syncing && 'animate-spin')} />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
        </div>
      </div>

      {syncResult && (
        <div
          className="mb-5 text-sm px-4 py-3 rounded-xl border text-ink-muted shadow-card"
          style={{ background: '#FDFCF8', borderColor: '#2A7A8A', boxShadow: '0 0 20px -8px rgba(42,122,138,0.2)' }}
        >
          {syncResult}
        </div>
      )}

      {loadError && (
        <div className="mb-5 text-sm px-4 py-3 rounded-xl border bg-[#fdf2f2] border-[#e8cfcf] text-red-800 flex flex-wrap items-center justify-between gap-3">
          <span>Could not load team list: {loadError}</span>
          <button
            type="button"
            onClick={() => void loadMembers()}
            className="text-xs font-medium px-3 py-1.5 rounded-lg border border-warm-border bg-warm-card hover:bg-warm-inset text-ink"
          >
            Retry
          </button>
        </div>
      )}

      {trackError && (
        <div className="mb-5 text-sm px-4 py-3 rounded-xl border bg-[#fdf2f2] border-[#e8cfcf] text-red-800">
          {trackError}
        </div>
      )}

      <div className="bg-warm-card border border-warm-border rounded-2xl overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-border">
                {columns.map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-medium text-ink-soft">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-warm-border/60">
                    {Array.from({ length: columns.length }).map((_, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-3 bg-warm-inset rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
                : members.map(m => {
                  const displayName = memberDisplayName(m)
                  return (
                  <tr
                    key={m.id}
                    className="border-b border-warm-border/60 hover:bg-warm-inset/40 transition-colors"
                  >
                    {/* Member */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-brand-teal flex items-center justify-center text-xs font-bold text-white shrink-0">
                          {displayName[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <p className="text-ink font-medium flex items-center gap-1.5">
                            {displayName}
                            {m.completed_this_month >= 4 && (
                              <span title={`${m.completed_this_month} actions this month`}>
                                <Medal 
                                  size={14} 
                                  className={clsx(
                                    m.completed_this_month >= 12 ? 'text-brand-teal' :
                                    m.completed_this_month >= 8 ? 'text-amber-500' : 'text-slate-400'
                                  )} 
                                />
                              </span>
                            )}
                          </p>
                          <p className="text-ink-soft text-xs">{m.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-5 py-3.5">
                      <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full border capitalize', ROLE_STYLES[m.role])}>
                        {m.role}
                      </span>
                    </td>

                    {/* Channel */}
                    <td className="px-5 py-3.5 text-ink-muted capitalize">{m.preferred_channel}</td>

                    {/* Frequency */}
                    <td className="px-5 py-3.5">
                      <span className={clsx('capitalize font-medium', FREQ_COLORS[m.nudge_frequency])}>
                        {m.nudge_frequency}
                      </span>
                    </td>

                    {/* Nudge Track */}
                    <td className="px-5 py-3.5">
                      {isManager ? (
                        <div className="relative">
                          <select
                            value={m.nudge_track ?? ''}
                            onChange={e => handleTrackChange(m.id, e.target.value || null)}
                            disabled={trackSaving === m.id}
                            className={clsx(
                              'text-xs font-medium rounded-full px-2.5 py-1 border cursor-pointer appearance-none pr-6 transition-opacity focus:outline-none focus:ring-1 focus:ring-brand-teal/50',
                              m.nudge_track
                                ? (TRACK_STYLES[m.nudge_track] ?? 'bg-warm-inset text-ink-muted border-warm-border')
                                : 'bg-warm-inset text-ink-faint border-warm-border',
                              trackSaving === m.id && 'opacity-50'
                            )}
                            style={{ backgroundImage: 'none' }}
                            title="Assign nudge track"
                          >
                            <option value="">— Auto</option>
                            {TRACK_OPTIONS.map(t => (
                              <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                          </select>
                          {/* Custom chevron */}
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-faint text-[10px]">
                            ▾
                          </span>
                          {trackSaving === m.id && (
                            <span className="absolute -right-5 top-1/2 -translate-y-1/2 text-ink-soft text-xs">…</span>
                          )}
                        </div>
                      ) : (
                        m.nudge_track ? (
                          <span className={clsx('text-xs font-medium px-2.5 py-0.5 rounded-full border capitalize', TRACK_STYLES[m.nudge_track])}>
                            {m.nudge_track}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-faint">Auto</span>
                        )
                      )}
                    </td>

                    {/* Connected */}
                    <td className="px-5 py-3.5">
                      {m.slack_user_id
                        ? <span className="text-xs text-emerald-800 font-medium">✓ Slack</span>
                        : <span className="text-xs text-ink-faint">—</span>}
                    </td>
                  </tr>
                  )
                })
              }
              {!loading && members.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-5 py-16 text-center">
                    <Users size={28} className="text-ink-faint mx-auto mb-3" />
                    <p className="text-ink-muted text-sm">No team members yet.</p>
                    <p className="text-ink-soft text-xs mt-1">Sync from Slack to import your workspace.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isManager && (
        <p className="mt-4 text-xs text-ink-faint">
          <span className="font-medium">Nudge Track:</span> Select a track to override AI assignment.
          Choose <em>Auto</em> to let Duva pick based on the member's engagement patterns.
        </p>
      )}
    </div>
  )
}
